import { SIGNALING_ADAPTER_EVENT_TYPE as SAET } from "./signalingAdapter.js";
/**
 * BrowserSignalingAdapter
 * -----------------------
 *
 * Real browser WebSocket signaling adapter.
 *
 * Responsibilities:
 * - open/close the underlying WebSocket
 * - send raw client->server protocol frames
 * - parse raw server->client protocol frames
 * - emit transport lifecycle events
 *
 * Non-responsibilities:
 * - HELLO/auth orchestration
 * - reconnect policy
 * - reducer/domain updates
 * - media/RTC logic
 */
export class BrowserSignalingAdapter {
    constructor(deps = {}) {
        this.socket = null;
        this.currentUrl = null;
        this.frameListeners = new Set();
        this.transportListeners = new Set();
        this.parseIncomingFrame =
            deps.parseIncomingFrame ??
                ((raw) => JSON.parse(raw));
        this.stringifyOutgoingFrame =
            deps.stringifyOutgoingFrame ??
                ((frame) => JSON.stringify(frame));
        this.createWebSocket =
            deps.createWebSocket ??
                ((url, protocols) => new WebSocket(url, protocols));
    }
    async connect(input) {
        if (this.socket) {
            await this.disconnect();
        }
        this.currentUrl = input.url;
        this.emitTransport({
            type: SAET.CONNECTING,
            url: input.url,
        });
        await new Promise((resolve, reject) => {
            const socket = this.createWebSocket(input.url, input.protocols);
            this.socket = socket;
            let settled = false;
            const cleanupPreOpenListeners = () => {
                socket.removeEventListener("open", onOpen);
                socket.removeEventListener("error", onErrorBeforeOpen);
                socket.removeEventListener("close", onCloseBeforeOpen);
            };
            const onOpen = () => {
                if (settled)
                    return;
                settled = true;
                cleanupPreOpenListeners();
                this.bindRuntimeSocketListeners(socket, input.url);
                this.emitTransport({
                    type: SAET.OPEN,
                    url: input.url,
                });
                resolve();
            };
            const onErrorBeforeOpen = (event) => {
                if (settled)
                    return;
                settled = true;
                cleanupPreOpenListeners();
                if (this.socket === socket) {
                    this.socket = null;
                }
                this.emitTransport({
                    type: SAET.ERROR,
                    error: event,
                });
                reject(new Error(`Failed to open signaling WebSocket: ${input.url}`));
            };
            const onCloseBeforeOpen = (event) => {
                if (settled)
                    return;
                settled = true;
                cleanupPreOpenListeners();
                if (this.socket === socket) {
                    this.socket = null;
                }
                this.emitTransport({
                    type: SAET.CLOSED,
                    ...(event.code !== undefined ? { code: event.code } : {}),
                    ...(event.reason !== "" ? { reason: event.reason } : {}),
                    ...(event.wasClean !== undefined ? { wasClean: event.wasClean } : {}),
                });
                reject(new Error(`Signaling WebSocket closed before opening: ${input.url} (${event.code})`));
            };
            socket.addEventListener("open", onOpen, { once: true });
            socket.addEventListener("error", onErrorBeforeOpen, { once: true });
            socket.addEventListener("close", onCloseBeforeOpen, { once: true });
        });
    }
    async disconnect() {
        const socket = this.socket;
        this.socket = null;
        if (!socket) {
            return;
        }
        await new Promise((resolve) => {
            if (socket.readyState === WebSocket.CLOSING ||
                socket.readyState === WebSocket.CLOSED) {
                resolve();
                return;
            }
            const onClose = () => {
                socket.removeEventListener("close", onClose);
                resolve();
            };
            socket.addEventListener("close", onClose, { once: true });
            try {
                socket.close(1000, "client disconnect");
            }
            catch {
                resolve();
            }
        });
    }
    async send(frame) {
        const socket = this.socket;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            return {
                ok: false,
                error: "Signaling transport is not connected",
            };
        }
        try {
            const raw = this.stringifyOutgoingFrame(frame);
            socket.send(raw);
            return { ok: true };
        }
        catch (err) {
            return {
                ok: false,
                error: err instanceof Error ? err.message : String(err),
            };
        }
    }
    isConnected() {
        return this.socket?.readyState === WebSocket.OPEN;
    }
    subscribeFrames(listener) {
        this.frameListeners.add(listener);
        return () => {
            this.frameListeners.delete(listener);
        };
    }
    subscribeTransport(listener) {
        this.transportListeners.add(listener);
        return () => {
            this.transportListeners.delete(listener);
        };
    }
    bindRuntimeSocketListeners(socket, url) {
        socket.addEventListener("message", (event) => {
            if (this.socket !== socket) {
                return;
            }
            if (typeof event.data !== "string") {
                this.emitTransport({
                    type: SAET.ERROR,
                    error: new Error("Expected text WebSocket frame from signaling server"),
                });
                return;
            }
            try {
                const frame = this.parseIncomingFrame(event.data);
                this.emitFrame(frame);
            }
            catch (err) {
                this.emitTransport({
                    type: SAET.ERROR,
                    error: err instanceof Error
                        ? err
                        : new Error(`Failed to parse signaling frame: ${String(err)}`),
                });
            }
        });
        socket.addEventListener("error", (event) => {
            if (this.socket !== socket) {
                return;
            }
            this.emitTransport({
                type: SAET.ERROR,
                error: event,
            });
        });
        socket.addEventListener("close", (event) => {
            if (this.socket === socket) {
                this.socket = null;
            }
            this.emitTransport({
                type: SAET.CLOSED,
                ...(event.code !== undefined ? { code: event.code } : {}),
                ...(event.reason !== "" ? { reason: event.reason } : {}),
                ...(event.wasClean !== undefined ? { wasClean: event.wasClean } : {}),
            });
            this.currentUrl = url;
        });
    }
    emitFrame(frame) {
        for (const listener of this.frameListeners) {
            listener(frame);
        }
    }
    emitTransport(event) {
        for (const listener of this.transportListeners) {
            listener(event);
        }
    }
}
